import { Box, Select, Title } from "@mantine/core";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { useRegulatoryDuties } from "../../hooks/useRegulatoryDuties";
import { TodoDescription } from "../../TodoDescription/TodoDescription";

interface RegulatoryDutySelectProps {
  disabled?: boolean;
  error?: string;
  id?: string;
  label?: string;
  value: number | null;
  onChange: (value: number | null) => void;
}

/**
 * Displays a dropdown with the list of regulatory duties in alphabetical order.
 * When a duty is selected, its description is shown as a preview below the dropdown.
 */
function RegulatoryDutySelect({
  disabled,
  error,
  id,
  label,
  value,
  onChange
}: RegulatoryDutySelectProps) {
  const {
    data: regulatoryDuties,
    isLoading,
    error: fetchError
  } = useRegulatoryDuties();

  function handleChange(newValue: string | null) {
    onChange(newValue ? parseInt(newValue) : null);
  }

  const sortedRegulatoryDuties = [...(regulatoryDuties || [])].sort((a, b) =>
    a.name < b.name ? -1 : 1
  );

  const options = [
    { value: "", label: "Keine regulatorische Pflicht" },
    ...sortedRegulatoryDuties.map((duty) => ({
      value: duty.id.toString(),
      label: duty.name
    }))
  ];

  const selectValue = value !== null ? value.toString() : "";

  return (
    <LoadOrError error={fetchError} loading={isLoading}>
      <Select
        data={options}
        disabled={disabled}
        error={error}
        id={id}
        label={label}
        value={selectValue}
        onChange={handleChange}
      />
      {value !== null && (
        <Box bd="1px solid neutral.3" m={10} p={15}>
          <Title order={6}>Beschreibung</Title>
          <TodoDescription
            regulatoryDuty={sortedRegulatoryDuties.find(
              (duty) => duty.id === value
            )}
          />
        </Box>
      )}
    </LoadOrError>
  );
}

export { RegulatoryDutySelect };
